import type { Storey } from '@shared/models/design/storey.model';
import {
  getStoreyById,
  getStoreyElevation,
  getStoreysBelow,
} from '@shared/helpers/design/storey.helpers';
import type { ThreeJsMode } from './types';

/**
 * Resolve which storeys to draw for a {@link ThreeJsMode}.
 */
export const resolveThreeJsTargetStoreys = (
  storeys: Storey[],
  selectedStoreyId: Storey['id'] | undefined,
  mode: ThreeJsMode,
): Storey[] => {
  switch (mode) {
    case 'all-storeys':
      return storeys;
    case 'selected-storey': {
      const selected = selectedStoreyId
        ? getStoreyById(storeys, selectedStoreyId)
        : undefined;
      return selected ? [selected] : [];
    }
    case 'selected-and-below': {
      const selected = selectedStoreyId
        ? getStoreyById(storeys, selectedStoreyId)
        : undefined;
      if (!selected) return [];
      // Include floors under the active storey; hide everything above so the plan is readable.
      return [...getStoreysBelow(storeys, selected), selected];
    }
  }
};

/**
 * Whether solid fills (floor/wall/roof) should draw for a storey when a PDF
 * underlay sits at {@link underlayElevationMeters}.
 * Floors strictly below the plan stay outline-only so the PDF stays dominant.
 */
export const shouldDrawStoreyFillsWithUnderlay = (
  storeys: Storey[],
  storeyId: Storey['id'],
  underlayElevationMeters: number | undefined,
): boolean => {
  if (underlayElevationMeters == null) return true;
  const storey = getStoreyById(storeys, storeyId);
  if (!storey) return true;
  return getStoreyElevation(storeys, storey) >= underlayElevationMeters - 1e-6;
};
